

"use client";

import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import SubscriptionAbout from "./SubscriptionAbout";
// import LorAuditPanel from "../components/LorAuditPanel";

const ACTIVE_STATES = ["active", "completed", "trialing"];

const SubscribePage = () => {
  const [email, setEmail] = useState("");
  const [verifyEmail, setVerifyEmail] = useState(""); // for already-subscribed users

  const [showMessage, setShowMessage] = useState(false);
  const [messageContent, setMessageContent] = useState("");

  const [loading, setLoading] = useState(false);        // subscribe flow
  const [verifying, setVerifying] = useState(false);    // verify-existing flow
  const [checkingSession, setCheckingSession] = useState(false); // post-checkout verify

  const [order, setOrder] = useState(null);
  const [justSubscribed, setJustSubscribed] = useState(false);

  const showMessageBox = (message) => {
    setMessageContent(message);
    setShowMessage(true);
  };

  const closeMessageBox = () => {
    setShowMessage(false);
    setMessageContent("");
  };

  // ✅ Verify the checkout session on return from Stripe (/subscribe?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    const verifyNewSubscription = async (session) => {
      try {
        setCheckingSession(true);
        const res = await fetch(`/api/orders?orderId=${encodeURIComponent(session)}`, {
          cache: "no-store",
        });
        const data = await res.json();

        if (res.ok && data.order) {
          const status = data.order.status;
          setOrder(data.order);

          // Only treat as subscribed if the order is actually active/completed/trialing
          if (ACTIVE_STATES.includes(status)) {
            if (data.order.email) {
              localStorage.setItem("userEmail", data.order.email);
            }
            localStorage.setItem("isSubscribed", "true");
            setJustSubscribed(true);
            // Option A: show success card then allow user to go to LOR
            // Option B: redirect immediately:
            // window.location.href = "/lor"; return;
          } else {
            showMessageBox(
              "Your payment is being processed. Please wait a moment and refresh, or contact support if this persists."
            );
          }
        } else {
          showMessageBox(data?.error || "Unable to verify your order. Please try again.");
        }
      } catch (err) {
        console.error("Error verifying checkout session:", err);
        showMessageBox("Network error while verifying your purchase. Please try again.");
      } finally {
        setCheckingSession(false);
      }
    };

    verifyNewSubscription(sessionId);
  }, []);

  const handleSubscribe = async () => {
    if (!email) {
      showMessageBox("Please enter your email to subscribe.");
      return;
    }

    try {
      setLoading(true);

      // ✅ Check first: if already active, skip checkout
      const checkRes = await fetch(`/api/orders?email=${encodeURIComponent(email)}`, {
        cache: "no-store",
      });
      const checkData = await checkRes.json();

      if (checkRes.ok && Array.isArray(checkData.orders)) {
        const activeOrder = checkData.orders.find((o) => ACTIVE_STATES.includes(o.status));
        if (activeOrder) {
          localStorage.setItem("isSubscribed", "true");
          localStorage.setItem("userEmail", email);
          window.location.href = "/lor";
          return;
        }
      }

      // Otherwise → create checkout session
      const response = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.url) {
        localStorage.setItem("userEmail", email);
        window.location.href = data.url;
      } else {
        showMessageBox("Failed to create checkout session.");
      }
    } catch (error) {
      console.error("Error during subscription:", error);
      showMessageBox("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Verify flow for existing subscribers (no checkout)
  const handleVerifyExisting = async () => {
    if (!verifyEmail) {
      showMessageBox("Please enter your subscribed email to continue.");
      return;
    }

    try {
      setVerifying(true);
      const res = await fetch(`/api/orders?email=${encodeURIComponent(verifyEmail)}`, {
        cache: "no-store",
      });
      const data = await res.json();

      if (res.ok && Array.isArray(data.orders)) {
        const activeOrder = data.orders.find((o) => ACTIVE_STATES.includes(o.status));
        if (activeOrder) {
          localStorage.setItem("isSubscribed", "true");
          localStorage.setItem("userEmail", verifyEmail);
          window.location.href = "/lor";
          return;
        }
      }

      showMessageBox(
        "We couldn't find an active subscription for this email. Please check the email you used at checkout or subscribe."
      );
    } catch (err) {
      console.error("Verification error:", err);
      showMessageBox("Unable to verify right now. Please try again in a moment.");
    } finally {
      setVerifying(false);
    }
  };

  // Optional success card after Stripe redirect verification
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 flex items-center justify-center p-4">
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl border border-gray-200 shadow-xl px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-3 text-slate-700">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span>Verifying your purchase…</span>
          </div>
        </div>
      </div>
    );
  }

  if (justSubscribed && order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 p-8 max-w-lg w-full text-center">
          <div className="inline-flex items-center justify-center rounded-full px-4 py-1.5 text-white font-semibold bg-gradient-to-r from-orange-600 to-red-700 mb-4">
            Subscription Active
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">🎉 You’re in!</h2>
          <p className="text-slate-700">
            Thanks for subscribing{order.email ? <> , <span className="font-semibold">{order.email}</span></> : null}.
          </p>
          <p className="text-gray-500 mt-2">
            Order ID: <span className="font-mono">{order.orderId}</span>
          </p>
          <a
            href="/lor"
            className="mt-6 inline-flex items-center px-6 py-3 rounded-full text-white font-semibold bg-gradient-to-r from-teal-600 to-orange-600 hover:from-teal-700 hover:to-orange-700 transition"
          >
            Go to Rec-Builder Generator
          </a>
          <a href="/" className="mt-4 block text-sm text-slate-500 hover:text-orange-600 transition-colors">
            &larr; Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
    <SubscriptionAbout/>
      {/* <LorAuditPanel/> */}
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 flex flex-col items-center justify-center p-4 sm:p-8 font-sans">
        <div className="text-center p-6 sm:p-10 bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl max-w-lg lg:max-w-xl w-full border border-gray-200 transition-all duration-300 ease-in-out transform hover:shadow-3xl">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-4">
            Unlock Full Access
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mb-8 max-w-md mx-auto">
         Subscribe to Rec-Builder to provide your students with high-quality, professional letters of recommendation that highlight their strengths and achievements.
          </p>

          {/* Plan card */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-inner border border-gray-100 flex flex-col gap-4 mb-6">
            <div className="text-left">
              <h3 className="text-xl sm:text-2xl font-bold text-teal-600">Premium Plan</h3>
              <p className="text-sm text-slate-500">Billed Monthly.</p>
            </div>
            <div className="flex items-baseline justify-center my-2 sm:my-4">
              <span className="text-5xl sm:text-6xl font-extrabold text-slate-900">$1</span>
              <span className="text-slate-600 text-lg sm:text-xl">/month</span>
            </div>
            <ul className="text-slate-700 list-disc list-inside space-y-2 text-left mb-2">
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span> Letter of Recommendations generation
              </li>
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span> Access to advanced AI features
              </li>
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span> Plagiarism Scanner
              </li>
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span> Resume Scanner
              </li>
            </ul>
          </div>

          {/* Subscribe input */}
          <label className="sr-only" htmlFor="subscribe-email">
            Email to subscribe
          </label>
          <input
            id="subscribe-email"
            type="email"
            placeholder="Enter your email to subscribe"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="px-6 py-3 rounded-full border border-slate-300 focus:ring-4 focus:ring-teal-200 focus:border-teal-400 outline-none text-slate-800 w-full transition-all duration-200 mb-4 text-center sm:text-left"
          />

          <button
            onClick={handleSubscribe}
            disabled={loading}
            className={`px-8 py-4 w-full rounded-full font-bold text-lg shadow-lg transition transform active:scale-95 ${
              loading
                ? "bg-slate-400 text-white cursor-not-allowed"
                : "bg-teal-600 text-white hover:bg-teal-700 hover:scale-105"
            }`}
          >
            {loading ? "Checking..." : "Subscribe Now"}
          </button>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs uppercase tracking-wide text-gray-500">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Already subscribed verification */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-inner">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Already subscribed?</h3>
            <p className="text-sm text-slate-600 mb-4">
              Enter the email you used at checkout to verify and continue.
            </p>

            <label className="sr-only" htmlFor="verify-email">
              Subscribed email
            </label>
            <input
              id="verify-email"
              type="email"
              placeholder="Enter your subscribed email"
              value={verifyEmail}
              onChange={(e) => setVerifyEmail(e.target.value)}
              className="px-5 py-2.5 rounded-xl border border-slate-300 focus:ring-4 focus:ring-teal-200 focus:border-teal-400 outline-none text-slate-800 w-full transition-all duration-200 mb-3"
            />

            <button
              onClick={handleVerifyExisting}
              disabled={verifying}
              className={`w-full rounded-xl px-4 py-2.5 font-semibold transition ${
                verifying
                  ? "bg-slate-400 text-white cursor-not-allowed"
                  : "bg-gradient-to-r from-teal-600 to-orange-600 text-white hover:from-teal-700 hover:to-orange-700"
              }`}
            >
              {verifying ? "Verifying..." : "Verify & Continue"}
            </button>
          </div>

          <a
            href="/"
            className="mt-8 block text-sm text-slate-500 hover:text-orange-600 transition-colors"
          >
            &larr; Back to Home
          </a>
        </div>

        {/* Custom Message Box */}
        {showMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-sm w-full relative transform scale-100 transition-transform duration-300 ease-out animate-popIn">
              <button
                onClick={closeMessageBox}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Close message"
              >
                <X size={24} />
              </button>
              <p className="text-lg text-slate-700 text-center">{messageContent}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SubscribePage;
